// src/components/block/liveStreamManagement/useScenePipelineManager.ts
// v1.1 — acquireSourceVideoEl reçoit currentStream pour emprunter la track vidéo
//         du flux final au lieu d'ouvrir un second getUserMedia sur la caméra.
//         Évite la saturation navigateur quand la webcam est en flux final ET
//         utilisée comme fond/incrustation d'une scène.

import { useRef, useCallback, useEffect } from 'react';
import { setVideoSource, releaseVideoSource } from '../../../dataTypes/liveStreamManagment/utils_videoSource';
import { LayoutSlot, VideoSource } from '../../../contexts/MediaStreamContext';
import { SourceRegistry } from '../../../dataTypes/liveStreamManagment/sourceRegistry/SourceRegistry';
import { BorrowHandle } from '../../../dataTypes/liveStreamManagment/sourceRegistry/types';

// ── Types internes ─────────────────────────────────────────────────────────────

interface ScenePipeline {
  layoutId      : string;
  canvas        : HTMLCanvasElement;
  stream        : MediaStream;
  timerId       : ReturnType<typeof setInterval>;
  bgImage       : HTMLImageElement | null;
  bgVideo       : HTMLVideoElement | null;
  incrImages    : Map<string, HTMLImageElement>;
  incrVideos    : Map<string, HTMLVideoElement>;
  sourceEls     : Map<string, HTMLVideoElement>;
  sourceHandles : Map<string, BorrowHandle>;   // ← remplace sourceOwned
}

export interface UseScenePipelineManagerReturn {
  /** Démarre (ou reconfigure) le pipeline d'une scène */
  activateScene        : (slot: LayoutSlot, videoSources: VideoSource[], currentStream: MediaStream | null) => Promise<void>;
  /** Arrête et détruit le pipeline d'une scène */
  deactivateScene      : (layoutId: string) => void;
  /** Retourne le MediaStream produit, ou null si inactif */
  getStream            : (layoutId: string) => MediaStream | null;
  /** True si le pipeline tourne */
  isActive             : (layoutId: string) => boolean;
  /**
   * Retourne les VideoSource synthétiques (type 'scene') pour les pipelines actifs.
   * À injecter dans le pool du deck via updateVideoSources().
   */
  getSceneVideoSources : (activeLayoutIds: string[], layoutSlots: LayoutSlot[]) => VideoSource[];
  /** Nettoyage global (unmount) */
  destroyAll           : () => void;
}

// ── Constantes ─────────────────────────────────────────────────────────────────

const CANVAS_W   = 1920;
const CANVAS_H   = 1080;
const TARGET_FPS = 30;

// ── Helpers ────────────────────────────────────────────────────────────────────

function computeContainRect(
  srcW: number, srcH: number,
  dstW: number, dstH: number,
): { x: number; y: number; w: number; h: number } {
  if (!srcW || !srcH) return { x: 0, y: 0, w: dstW, h: dstH };
  const scale = Math.min(dstW / srcW, dstH / srcH);
  const w = srcW * scale, h = srcH * scale;
  return { x: (dstW - w) / 2, y: (dstH - h) / 2, w, h };
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    if (!url.startsWith('data:')) img.crossOrigin = 'anonymous';
    img.onload  = () => resolve(img);
    img.onerror = () => {
      // Retry sans CORS
      const img2 = new window.Image();
      img2.onload  = () => resolve(img2);
      img2.onerror = reject;
      img2.src = url.includes('?') ? `${url}&_nc=1` : `${url}?_nc=1`;
    };
    img.src = url;
  });
}

function createLoopingVideo(url: string): HTMLVideoElement {
  const vid       = document.createElement('video');
  setVideoSource(vid, url);
  vid.autoplay    = true;
  vid.loop        = true;
  vid.muted       = true;
  vid.playsInline = true;
  vid.style.display = 'none';
  document.body.appendChild(vid);
  vid.play().catch(() => {});
  return vid;
}

function destroyVideoEl(vid: HTMLVideoElement): void {
  releaseVideoSource(vid);
  vid.pause();
  vid.src  = '';
  vid.load();
  vid.remove();
}

/**
 * Crée un <video> qui lit le flux d'une VideoSource du deck.
 *
 * Pour les sources 'device' : on emprunte la track vidéo du flux final
 * (currentStream) si disponible, évitant un second getUserMedia qui saturait
 * le navigateur. Fallback getUserMedia si la caméra n'est pas en flux final.
 *
 * owned = true  → on possède le stream, il faut stopper les tracks à la destruction
 * owned = false → on emprunte une track existante, on ne la stoppe pas
 */

// ── Draw loop ──────────────────────────────────────────────────────────────────

function buildDrawLoop(
  pipeline : ScenePipeline,
  slot     : LayoutSlot,
): ReturnType<typeof setInterval> {
  const canvas = pipeline.canvas;
  const ctx    = canvas.getContext('2d')!;
  const w = canvas.width, h = canvas.height;

  return setInterval(() => {
    // Fond noir par défaut
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, w, h);

    // ── Fond ──────────────────────────────────────────────────────────────────
    const bg = slot.background;

    if (bg.type === 'image' && pipeline.bgImage?.complete && pipeline.bgImage.naturalWidth > 0) {
      const r = computeContainRect(pipeline.bgImage.naturalWidth, pipeline.bgImage.naturalHeight, w, h);
      ctx.drawImage(pipeline.bgImage, r.x, r.y, r.w, r.h);

    } else if (bg.type === 'video' && pipeline.bgVideo && pipeline.bgVideo.readyState >= 2) {
      const r = computeContainRect(pipeline.bgVideo.videoWidth, pipeline.bgVideo.videoHeight, w, h);
      ctx.drawImage(pipeline.bgVideo, r.x, r.y, r.w, r.h);

    } else if (bg.type === 'source' && bg.sourceId) {

      const vid = pipeline.sourceEls.get(bg.sourceId);
      if (vid && vid.readyState >= 2) {
        const r = computeContainRect(vid.videoWidth, vid.videoHeight, w, h);
        ctx.drawImage(vid, r.x, r.y, r.w, r.h);
      }
    }

    // ── Incrustations (triées par zIndex croissant) ───────────────────────────
    const sorted = [...slot.incrustations].sort((a, b) => a.zIndex - b.zIndex);

    for (const ic of sorted) {
      const ix = (ic.x      / 100) * w;
      const iy = (ic.y      / 100) * h;
      const iw = (ic.width  / 100) * w;
      const ih = (ic.height / 100) * h;

      ctx.save();
      ctx.globalAlpha = ic.opacity;

      if (ic.type === 'image') {
        const img = pipeline.incrImages.get(ic.id);
        if (img?.complete && img.naturalWidth > 0) {
          ctx.drawImage(img, ix, iy, iw, ih);
        }

      } else if (ic.type === 'video') {
        const vid = pipeline.incrVideos.get(ic.id);
        if (vid && vid.readyState >= 2) {
          ctx.drawImage(vid, ix, iy, iw, ih);
        }

      } else if (ic.type === 'source') {
  console.log('[drawLoop] ic.id:', ic.id, 'ic.sourceId:', ic.sourceId, 'sourceEls keys:', Array.from(pipeline.sourceEls.keys()), 'vid trouvée:', !!pipeline.sourceEls.get(ic.sourceId));
        const vid = pipeline.sourceEls.get(ic.sourceId);
    console.log('[drawLoop] ic.sourceId:', ic.sourceId, 'vid.srcObject id:', (vid?.srcObject as MediaStream)?.id, 'tracks:', (vid?.srcObject as MediaStream)?.getVideoTracks().map(t => t.label));
      if (vid && vid.readyState >= 2) {
          ctx.drawImage(vid, ix, iy, iw, ih);
        }
      }

      ctx.restore();
    }
  }, 1000 / TARGET_FPS) as ReturnType<typeof setInterval>;
}

// ── Destruction d'un pipeline ──────────────────────────────────────────────────

function destroyPipelineInternal(pipeline: ScenePipeline): void {
  clearInterval(pipeline.timerId);
  pipeline.stream.getTracks().forEach(t => t.stop());

  pipeline.bgImage = null;
  if (pipeline.bgVideo) destroyVideoEl(pipeline.bgVideo);

  pipeline.incrImages.clear();
  pipeline.incrVideos.forEach(v => destroyVideoEl(v));
  pipeline.incrVideos.clear();

  pipeline.sourceEls.forEach(vid => {
    vid.srcObject = null;
    vid.pause();
    vid.remove();
  });
  pipeline.sourceEls.clear();

  // Libérer les handles via le registry (décrémente refCount)
  pipeline.sourceHandles.forEach(handle => {
    try { handle.release(); } catch {}
  });
  pipeline.sourceHandles.clear();
}

// ── Hook ───────────────────────────────────────────────────────────────────────

export function useScenePipelineManager(registry: SourceRegistry): UseScenePipelineManagerReturn {

  // Map layoutId → ScenePipeline actif
  const pipelinesRef = useRef<Map<string, ScenePipeline>>(new Map());

  // ── activateScene ──────────────────────────────────────────────────────────

  const activateScene = useCallback(async (
    slot         : LayoutSlot,
    videoSources : VideoSource[],
    currentStream: MediaStream | null,
  ): Promise<void> => {

  console.log(`[activateScene] START ${slot.id} — existing: ${!!pipelinesRef.current.get(slot.id)}`);

    const existing = pipelinesRef.current.get(slot.id);

    // Si un pipeline existe déjà, on stoppe sa loop et on libère ses assets
    // mais on RÉUTILISE le canvas et le stream pour éviter une coupure du flux.
if (existing) {
  clearInterval(existing.timerId);
  existing.bgImage = null;
  if (existing.bgVideo) destroyVideoEl(existing.bgVideo);
  existing.incrImages.clear();
  existing.incrVideos.forEach(v => destroyVideoEl(v));
  existing.incrVideos.clear();
  existing.sourceEls.forEach(vid => {
    vid.srcObject = null;
    vid.pause();
    vid.remove();
  });
  existing.sourceEls.clear();
  existing.sourceHandles.forEach(handle => { try { handle.release(); } catch {} });
  existing.sourceHandles.clear();
}

    // Canvas : réutilisé si existant, créé sinon
    const canvas  = existing?.canvas ?? document.createElement('canvas');
    canvas.width  = CANVAS_W;
    canvas.height = CANVAS_H;

    // Collecte des sourceIds nécessaires (dédupliqués)
    const neededSourceIds = new Set<string>();
    if (slot.background.type === 'source' && slot.background.sourceId)
      neededSourceIds.add(slot.background.sourceId);
    for (const ic of slot.incrustations)
      if (ic.type === 'source' && ic.sourceId)
        neededSourceIds.add(ic.sourceId);

    // Structures à remplir en parallèle
    let bgImage    : HTMLImageElement | null = null;
    let bgVideo    : HTMLVideoElement | null = null;
    const incrImages  = new Map<string, HTMLImageElement>();
    const incrVideos  = new Map<string, HTMLVideoElement>();
    const sourceEls   = new Map<string, HTMLVideoElement>();
    const sourceHandles = new Map<string, BorrowHandle>();

console.log('[activateScene] currentStream tracks:', 
  currentStream?.getTracks().map(t => `${t.kind}:${t.label}`)
);


    // Chargement parallèle de tous les assets
    await Promise.all([

      // Fond image
      slot.background.type === 'image' && slot.background.url
        ? loadImage(slot.background.url)
            .then(img => { bgImage = img; })
            .catch(() => {})
        : Promise.resolve(),

      // Fond vidéo (loop)
      slot.background.type === 'video' && slot.background.url
        ? Promise.resolve().then(() => {
            bgVideo = createLoopingVideo(slot.background.url);
          })
        : Promise.resolve(),

      // Incrustations image
      ...slot.incrustations
        .filter(ic => ic.type === 'image' && ic.url)
        .map(ic =>
          loadImage(ic.url)
            .then(img => incrImages.set(ic.id, img))
            .catch(() => {}),
        ),

      // Incrustations vidéo (loop)
      ...slot.incrustations
        .filter(ic => ic.type === 'video' && ic.url)
        .map(ic =>
          Promise.resolve().then(() => {
            incrVideos.set(ic.id, createLoopingVideo(ic.url));
          }),
        ),

      // Sources deck — guard anti-boucle : refus des types 'scene' et 'jingle'
      // currentStream transmis pour emprunter la track caméra sans getUserMedia
// Sources deck — dans Promise.all([...])
...Array.from(neededSourceIds).map(async sourceId => {
  console.log('[activateScene] sourceId demandé:', sourceId);
  console.log('[activateScene] videoSources disponibles:', videoSources.map(s => s.id));
  const src = videoSources.find(
    s => s.id === sourceId && s.type !== 'scene' && s.type !== 'jingle',
  );
  console.log('[activateScene] src trouvée:', src?.id ?? 'NULL');
  
//  if (!src) return;

    // Ne pas filtrer sur src : le registry est la source de vérité.
    // Un sourceId peut être dans le registry sans être dans videoSources
    // (ex. source enregistrée mais non déclarée dans le context React).
    if (src?.type === 'scene' || src?.type === 'jingle') return; // guard conservé
    const stateInRegistry = registry.getState(sourceId);
    if (stateInRegistry === 'idle' && !src) return; // source inconnue partout


  try {
	console.log('[activateScene] avant borrow', sourceId,
  '→ state:', registry.getState(sourceId),
  '→ stream existant:', registry.getStream(sourceId)?.getTracks().map(t => `${t.kind}:${t.label}`)
);  
    const handle = await registry.borrow(sourceId);
console.log('[activateScene] borrow', sourceId, 
  '→ state:', registry.getState(sourceId),
  '→ stream id:', handle.stream.id,
  '→ tracks:', handle.stream.getTracks().map(t => `${t.kind}:${t.label}`)
);
    const vid = document.createElement('video');
    vid.srcObject   = handle.stream;
    vid.autoplay    = true;
    vid.muted       = true;
    vid.playsInline = true;
    vid.style.display = 'none';
    document.body.appendChild(vid);
    await vid.play().catch(() => {});
	
	console.log('[activateScene] vid créée pour', sourceId,
  '→ srcObject id:', (vid.srcObject as MediaStream)?.id,
  '→ tracks:', (vid.srcObject as MediaStream)?.getTracks().map(t => `${t.kind}:${t.label}`)
);
    sourceEls.set(sourceId, vid);
    sourceHandles.set(sourceId, handle);
  } catch { 
    // source indisponible, on ignore
  }
}),

    ]);

    // Stream : réutilisé si existant (pas de coupure), capturé sinon
    const stream = existing?.stream ?? canvas.captureStream(TARGET_FPS);

    const pipeline: ScenePipeline = {
      layoutId : slot.id,
      canvas,
      stream,
      timerId  : null as any,  // assigné juste en dessous
      bgImage,
      bgVideo,
      incrImages,
      incrVideos,
      sourceEls,
      sourceHandles,
    };

    pipeline.timerId = buildDrawLoop(pipeline, slot);
    pipelinesRef.current.set(slot.id, pipeline);

  console.log(`[activateScene] END ${slot.id} — sourceEls:`, Array.from(sourceEls.keys()));

  }, []);

  // ── deactivateScene ────────────────────────────────────────────────────────

  const deactivateScene = useCallback((layoutId: string) => {
    const pipeline = pipelinesRef.current.get(layoutId);
    if (!pipeline) return;
    destroyPipelineInternal(pipeline);
    pipelinesRef.current.delete(layoutId);
  }, []);

  // ── getStream ──────────────────────────────────────────────────────────────

  const getStream = useCallback(
    (layoutId: string): MediaStream | null =>
      pipelinesRef.current.get(layoutId)?.stream ?? null,
    [],
  );

  // ── isActive ───────────────────────────────────────────────────────────────

  const isActive = useCallback(
    (layoutId: string): boolean => pipelinesRef.current.has(layoutId),
    [],
  );

  // ── getSceneVideoSources ───────────────────────────────────────────────────

  const getSceneVideoSources = useCallback((
    activeLayoutIds: string[],
    layoutSlots    : LayoutSlot[],
  ): VideoSource[] =>
    activeLayoutIds.flatMap(layoutId => {
      const pipeline = pipelinesRef.current.get(layoutId);
      if (!pipeline) return [];
      const slot  = layoutSlots.find(s => s.id === layoutId);
      const label = slot?.label ?? layoutId;
      return [{
        id    : `scene-${layoutId}`,
        label : `🎬 ${label}`,
        type  : 'scene' as const,
        stream: pipeline.stream,
      }];
    }),
  []);

  // ── destroyAll ─────────────────────────────────────────────────────────────

  const destroyAll = useCallback(() => {
    pipelinesRef.current.forEach(p => destroyPipelineInternal(p));
    pipelinesRef.current.clear();
  }, []);

  // Cleanup au démontage du composant
  useEffect(() => () => destroyAll(), [destroyAll]);

  return {
    activateScene,
    deactivateScene,
    getStream,
    isActive,
    getSceneVideoSources,
    destroyAll,
  };
}
